import { fetchTTSAudioUrls } from "../api";
import {
  ACCEPT_ALL_BUTTON_ID,
  ACCEPT_SELECTED_BUTTON_ID,
  DECLINE_BUTTON_ID,
  DPO_AUDIO_ELEMENT_MAP,
} from "../constants";
import { purposesInRenderOrder } from "../core/productMatrix";
import type {
  ActiveConfig,
  PurposeItemData,
  SandboxOptions,
  TTSAudioUrlsResponse,
} from "../api/types";
import type { EndpointMode } from "../shared/endpoints";
import type { TTSAudioFetcher } from "../shared/types";

export class TTSManager {
  private audioUrls: TTSAudioUrlsResponse["detail"] | null = null;
  private audioSequence: string[] = [];
  private elementIdSequence: string[] = [];
  private currentAudio: HTMLAudioElement | null = null;
  private currentIndex = 0;
  private _isPlaying = false;
  private _isPaused = false;
  private _isAvailable = false;
  private highlightedElementId: string | null = null;
  private abortController: AbortController | null = null;
  private onStateChange: () => void;
  private root: Document | ShadowRoot;
  private fetchAudio: TTSAudioFetcher;

  /**
   * @param onStateChange Invoked whenever playback state changes so the host
   *   can refresh its UI (e.g. swap the play/pause icon).
   * @param root The root in which to look up highlightable elements by id.
   *   When the modal is rendered inside a shadow root, pass that shadow root
   *   so queries stay scoped to the SDK's tree (and don't accidentally hit
   *   element ids that the host page also defines).
   */
  constructor(
    onStateChange: () => void,
    root: Document | ShadowRoot,
    fetchAudio: TTSAudioFetcher = fetchTTSAudioUrls
  ) {
    this.onStateChange = onStateChange;
    this.root = root;
    this.fetchAudio = fetchAudio;
  }

  get isPlaying(): boolean { return this._isPlaying; }
  get isPaused(): boolean { return this._isPaused; }
  get isAvailable(): boolean { return this._isAvailable; }
  get currentElementId(): string | null { return this.highlightedElementId; }

  async fetchAudioUrls(params: SandboxOptions & {
    accessToken?: string;
    baseUrl?: string;
    ledgerBaseUrl?: string;
    endpointMode?: EndpointMode;
    noticeUuid: string;
    language: string;
    content: ActiveConfig;
    /** The purposes in the order the notice shows them; render order when absent. */
    purposes?: ReadonlyArray<PurposeItemData>;
  }): Promise<void> {
    // Reset availability + sequences SYNCHRONOUSLY before notifying. The
    // language can change while a previous language's URLs are still loaded;
    // if we don't clear here, a click on the speaker icon (or the in-flight
    // stop()) would either keep the icon showing or replay the previous
    // language's audio. We then fire a single onStateChange() so the UI hides
    // the icon while the new language's audio is being fetched.
    this.stopInternal();
    this._isAvailable = false;
    this.audioUrls = null;
    this.audioSequence = [];
    this.elementIdSequence = [];
    this.onStateChange();

    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const response = await this.fetchAudio({
        accessToken: params.accessToken,
        baseUrl: params.baseUrl,
        ledgerBaseUrl: params.ledgerBaseUrl,
        endpointMode: params.endpointMode,
        noticeUuid: params.noticeUuid,
        language: params.language,
        signal: this.abortController.signal,
        token: params.token,
        sandboxUcic: params.sandboxUcic,
        sandboxEmail: params.sandboxEmail,
        sandboxMobile: params.sandboxMobile,
        organisationUuid: params.organisationUuid,
        workspaceUuid: params.workspaceUuid,
      });

      this.audioUrls = response.detail;
      this.buildSequences(params.content, params.purposes);
      // Only mark TTS as available if the backend actually returned playable
      // audio for this language. Some languages (e.g. Santali) come back with
      // a 200 but empty / partial fields — surfacing the speaker icon in that
      // case would let users click it and replay the previous language.
      this._isAvailable = this.audioSequence.length > 0;
      this.onStateChange();
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        this._isAvailable = false;
        this.audioUrls = null;
        this.audioSequence = [];
        this.elementIdSequence = [];
        this.onStateChange();
      }
    }
  }

  private buildSequences(
    content: ActiveConfig,
    ordered?: ReadonlyArray<PurposeItemData>
  ): void {
    if (!this.audioUrls) return;
    const urls = this.audioUrls;

    const audioSeq: string[] = [];
    const elementSeq: string[] = [];

    const push = (url: string | undefined, elementId: string) => {
      if (url) {
        audioSeq.push(url);
        elementSeq.push(elementId);
      }
    };

    // 1. Notice banner heading
    push(urls.notice_banner_heading_audio_url, "privacy-notice-title");
    // 2. Notice text
    push(urls.notice_text_audio_url, "privacy-notice-text");
    // 3. Privacy policy prefix text
    push(urls.privacy_policy_prefix_text_audio_url, "privacy-policy-text");
    // 4. Privacy policy anchor text
    push(urls.privacy_policy_anchor_text_audio_url, "privacy-policy-link");
    // 5. Purpose section heading
    push(urls.purpose_section_heading_audio_url, "purpose-section-heading");

    // 6. For each purpose, in the order the visitor sees them: name,
    // description, data elements.
    const purposes =
      ordered ??
      purposesInRenderOrder(
        content.products,
        content.purposes,
        content.product_purpose_order
      );
    for (const purpose of purposes) {
      const purposeAudio = urls.purposes_audio?.[purpose.uuid];
      if (purposeAudio) {
        push(purposeAudio.name_audio_url, `purpose-name-${purpose.uuid}`);
        push(purposeAudio.description_audio_url, `purpose-description-${purpose.uuid}`);
      }

      for (const de of purpose.data_elements) {
        const deAudio = urls.data_elements_audio?.[de.uuid];
        if (deAudio) {
          push(deAudio.name_audio_url, `data-element-${purpose.uuid}-${de.uuid}`);
        }
      }
    }

    // 7. Additional text, when the notice shows any
    if (content.additional_text) {
      push(urls.additional_text_audio_url, "additional-text");
    }
    // 8. Privacy center anchor text, when the notice links one
    if (content.privacy_center_url) {
      push(urls.privacy_center_anchor_text_audio_url, "privacy-center-link");
    }

    // 9. DPO info audio
    if (urls.dpo_info_audio) {
      for (const mapping of DPO_AUDIO_ELEMENT_MAP) {
        const audioUrl = urls.dpo_info_audio[mapping.audioKey];
        push(audioUrl, mapping.elementId);
      }
    }

    // 10. Action buttons, read in the order they are laid out.
    push(urls.accept_all_button_text_audio_url, ACCEPT_ALL_BUTTON_ID);
    push(urls.confirm_button_text_audio_url, ACCEPT_SELECTED_BUTTON_ID);
    push(urls.decline_button_text_audio_url, DECLINE_BUTTON_ID);

    this.audioSequence = audioSeq;
    this.elementIdSequence = elementSeq;
  }

  toggle(): void {
    this._isPlaying ? this.pause() : this.play();
  }

  play(): void {
    if (this.audioSequence.length === 0) return;

    if (this._isPaused && this.currentAudio) {
      this.currentAudio.play()
        .then(() => {
          this._isPlaying = true;
          this._isPaused = false;
          this.onStateChange();
        })
        .catch(() => {});
    } else {
      const startIndex = this._isPlaying ? this.currentIndex : 0;
      this.playNextAudio(startIndex);
    }
  }

  pause(): void {
    if (this.currentAudio && this._isPlaying) {
      this.currentAudio.pause();
      this._isPlaying = false;
      this._isPaused = true;
      this.onStateChange();
    }
  }

  stop(): void {
    this.stopInternal();
    this.onStateChange();
  }

  /**
   * Internal stop used by code paths (like fetchAudioUrls) that will fire
   * their own onStateChange after composing a richer state update — we don't
   * want the intermediate notification to flicker the UI based on a half-set
   * state (e.g. _isAvailable still true from the previous language).
   */
  private stopInternal(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    this._isPlaying = false;
    this._isPaused = false;
    this.currentIndex = 0;
    this.removeHighlights();
    this.highlightedElementId = null;
  }

  destroy(): void {
    this.abortController?.abort();
    this.stop();
  }

  private playNextAudio(index: number): void {
    if (index >= this.audioSequence.length) {
      this.stop();
      return;
    }

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }

    const audio = new Audio(this.audioSequence[index]);
    this.currentAudio = audio;
    this.currentIndex = index;
    this._isPlaying = true;
    this._isPaused = false;

    // Highlight
    const elementId = this.elementIdSequence[index];
    if (elementId) {
      this.highlightElement(elementId);
    }

    audio.onended = () => this.playNextAudio(index + 1);
    audio.onerror = () => {
      console.warn(`Failed to load audio at index ${index}`);
      this.playNextAudio(index + 1);
    };

    audio.play().catch((error) => {
      console.warn(`Failed to play audio at index ${index}:`, error);
      this._isPlaying = false;
      this.onStateChange();
    });

    this.onStateChange();
  }

  private highlightElement(elementId: string): void {
    this.removeHighlights();
    this.highlightedElementId = elementId;

    setTimeout(() => {
      // ShadowRoot has getElementById in modern browsers; fall back to
      // querySelector with CSS.escape for safety.
      const element =
        "getElementById" in this.root
          ? (this.root as Document | ShadowRoot & { getElementById(id: string): HTMLElement | null }).getElementById(elementId)
          : (this.root as ShadowRoot).querySelector<HTMLElement>(`#${CSS.escape(elementId)}`);
      if (element) {
        element.classList.add("redacto-tts-highlight");

        const rect = element.getBoundingClientRect();
        const isVisible =
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth;

        if (!isVisible) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }
    }, 50);
  }

  private removeHighlights(): void {
    this.root.querySelectorAll(".redacto-tts-highlight").forEach((el) => {
      el.classList.remove("redacto-tts-highlight");
    });
  }
}
